import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { StatusBar } from '@capacitor/status-bar';
import { filter } from 'rxjs';
import { urlAllowsDock } from './menu-style.service';
import { UserService } from './user.service';
import { pageScrollEl } from '../commons/utils/page-scroll';

const KEY = 'llShell';
/** People who get the app-frame layout without doing anything. Everyone else keeps the normal page layout. */
const SHELL_USERS = ['punj@rikexim.com'];
/** Phones and tablets (the problem is the browser toolbar of iPhone and iPad); desktops keep the normal layout. */
const MAX_WIDTH = 1024;

/**
 * The app frame (2026-10-10, switched on per person while it is tested on real iPhones).
 *
 * Why: on iPhone Safari the bottom dock is a fixed bar, and when Safari's toolbar collapses or expands (at the end of a scroll, or on a short
 * page) the bar slid up or left a white gap, and the content showed behind the clock. With the frame, the document itself never scrolls:
 * <html> and <body> stay still, and the page content scrolls inside  app-root > .container. A fixed bar then stays exactly where it is.
 *
 * What it does: puts the class  ll-shell  on <html> (all the CSS is in styles.css under that class) while the frame is on and the page is
 * not a tally page. Nothing else changes for anyone who is not switched on.
 *
 * ── WHAT MADE THE iPHONE APP WORK (2026-10-10, found on a real iPhone 14, iOS 26.0.1, TestFlight). READ THIS BEFORE TOUCHING THE HEADER OR THE DOCK. ──
 * The header (top bar) and the bottom dock are FINISHED and tested on the phone and in Safari. Do not change their layout, height, padding, position or
 * colours. If something looks wrong on a screen, fix that screen, not these two bars. What made them right, in short:
 *   1. The page never scrolls as a document (html/body overflow hidden, class ll-shell). Only app-root > .container scrolls. That alone fixed the content
 *      showing behind the clock and the dock sliding at the end of a scroll, in Safari and in the app.
 *   2. Safari: the frame stops where the dock starts (app-root bottom = --app-dock-h) and the dock has ONE fixed height (64px + 28px), not the safe-area
 *      value, which flips between 0 and about 34px while Safari's toolbar collapses.
 *   3. The app (class ll-native, only on the iPhone app): the web view cut the dock off at the frame's bottom edge, so the frame runs the full height
 *      (bottom: 0) and the scroller keeps the dock's height as bottom padding; the dock has a solid background (no blur).
 *   4. The app: capacitor.config.ts has contentInset 'never' and the status bar plugin overlays the web view, so the PAGE paints the strips behind the clock
 *      and under the dock in the theme colours (white / #18181b). The native window colour is a fixed white and cannot follow the theme.
 *   5. The app: index.html drops viewport-fit=cover on iPhone, so env(safe-area-inset-*) reads 0 there. The clock area height therefore comes from the
 *      status bar plugin (StatusBar.getInfo().height, 47 on an iPhone 14) into --ll-status-h, and the frame's top padding uses that. The dock's bottom
 *      padding also uses env(), which is why the viewport tag must NOT be changed (it would change the dock).
 *   6. The app: StatusBarService sets the clock text colour from the APP theme, because the page paints the strip.
 * The app ships its own copy of the web files, so a change here reaches the installed app only through a new Codemagic build.
 *
 * Switch: the e-mail list above turns it on by itself. On one device, open any page with  ?shell=1  (on),  ?shell=0  (off) or  ?shell=auto
 * (back to the list). The choice is kept on that device only.
 */
@Injectable({ providedIn: 'root' })
export class ShellLayoutService {
  private readonly router = inject(Router);
  private readonly users = inject(UserService);

  /** The installed iPhone app (TestFlight only for now). Browsers and Android never take the native paths below. */
  private readonly iosApp = typeof window !== 'undefined' && Capacitor.getPlatform() === 'ios';

  private readonly email = signal<string | null>(null);
  private readonly url = signal(this.router.url);
  private readonly width = signal(typeof window !== 'undefined' ? window.innerWidth : 1280);
  /** '1' on, '0' off, null = follow the e-mail list. */
  private readonly choice = signal<'0' | '1' | null>(ShellLayoutService.readChoice());

  readonly enabled = computed(() => {
    const c = this.choice();
    if (c === '1') return true;
    if (c === '0') return false;
    // The installed iPhone app: on for everyone (it is only on TestFlight; the e-mail never reaches this service in the app, see the notes in
    // AppComponent / HeaderComponent about localStorage.user). Browsers still follow the e-mail list.
    if (this.iosApp) return true;
    const mail = this.email();
    return !!mail && SHELL_USERS.includes(mail);
  });
  /** On for this person, on a phone or tablet, and not on a tally page (those keep their own scrolling and bars). */
  readonly active = computed(() => this.enabled() && this.width() <= MAX_WIDTH && urlAllowsDock(this.url()));

  constructor() {
    this.users.currentUser$.subscribe((u) => this.email.set((u?.email ?? '').trim().toLowerCase() || null));
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
      this.url.set(e.urlAfterRedirects || e.url);
      // a new page starts at its top, like the browser does when the document scrolls
      if (this.active()) requestAnimationFrame(() => { const el = pageScrollEl(); if (el) el.scrollTop = 0; });
    });
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        const w = window.innerWidth;
        if (w !== this.width()) this.width.set(w);
      });
    }
    effect(() => {
      document.documentElement.classList.toggle('ll-shell', this.active());
    });
    // The installed iPhone app only (never a browser): a class for native-only CSS (styles.css, html.ll-native). See the big note at the top of this file.
    if (this.iosApp) {
      document.documentElement.classList.add('ll-native');
      // The page's own safe-area value reads 0 in the app (index.html drops viewport-fit=cover on iPhone), so the clock area height is taken from
      // the status bar plugin instead and kept in --ll-status-h (styles.css falls back to 47px, an iPhone 14, until this arrives).
      const readStatusHeight = (): void => {
        StatusBar.getInfo()
          .then((i) => {
            const h = (i as unknown as { height?: number }).height;
            if (typeof h === 'number' && h >= 0) document.documentElement.style.setProperty('--ll-status-h', h + 'px');
          })
          .catch(() => { /* keep the 47px fallback */ });
      };
      readStatusHeight();
      window.addEventListener('resize', readStatusHeight);
      window.addEventListener('orientationchange', readStatusHeight);
    }
  }

  /** Reads ?shell=1|0|auto once (and keeps it on this device), then the stored choice. */
  private static readChoice(): '0' | '1' | null {
    try {
      const q = new URLSearchParams(window.location.search).get('shell');
      if (q === '1' || q === '0') localStorage.setItem(KEY, q);
      else if (q === 'auto') localStorage.removeItem(KEY);
      const v = localStorage.getItem(KEY);
      return v === '1' || v === '0' ? v : null;
    } catch {
      return null; // storage blocked: the e-mail list decides
    }
  }
}
